import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BarChart3 } from "lucide-react";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import { useAccount } from "@/contexts/account.context";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CarregandoValor,
  ValorIndisponivel,
} from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
  ResponsiveContainer,
} from "recharts";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import { formatDateBR, formatMonthLabel } from "@/utils/dateFormat";
import {
  formatCurrency as formatCurrencyUtil,
  formatInteger,
} from "@/utils/numberFormat";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import { KpiNumber } from "@/components/ui/kpi-number";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";
import { getRevenueDashboardApiV1RevenueDashboardGet } from "@/backend-sdk/client/sdk.gen";
import type {
  RevenueDashboardKpis,
  RevenueDashboardPerDay as GeneratedRevenueDashboardPerDay,
  RevenueDashboardPerHour as GeneratedRevenueDashboardPerHour,
  RevenueDashboardPerMonth as GeneratedRevenueDashboardPerMonth,
  RevenueDashboardPerWeekday as GeneratedRevenueDashboardPerWeekday,
} from "@/backend-sdk/client/types.gen";

/** A receita total do card sai do e-commerce do GA4. */
const FONTES_DA_RECEITA: readonly RequisitoDeFonte[] = ["ga4"];

// Dados e estado preenchidos via API real
type Kpis = RevenueDashboardKpis;
type RevenueDashboardPerDay = GeneratedRevenueDashboardPerDay;
type RevenueDashboardPerWeekday = GeneratedRevenueDashboardPerWeekday;
type RevenueDashboardPerHour = GeneratedRevenueDashboardPerHour;
type RevenueDashboardPerMonth = GeneratedRevenueDashboardPerMonth;

type RevenueChartOptions = {
  receitaTotal: boolean;
  comprasEcommerce: boolean;
  investimentoGoogle: boolean;
  investimentoMeta: boolean;
  roas: boolean;
  investimentoTotal: boolean;
};

interface DailyRevenueChartRow {
  data: string;
  receitaTotal: number;
  comprasEcommerce: number;
  investimentoGoogle: number;
  investimentoMeta: number;
  roas: number;
  investimentoTotal?: number;
}

interface WeekdayRevenueChartRow {
  dia: string;
  receitaTotal: number;
  comprasEcommerce: number;
  investimentoGoogle: number;
  investimentoMeta: number;
  roas: number;
}

interface HourRevenueChartRow {
  hora: string;
  receitaTotal: number;
  comprasEcommerce: number;
}

interface MonthRevenueChartRow {
  mes: string;
  receitaTotal: number;
  transacoes: number;
}

interface RechartsTooltipEntry {
  dataKey?: string | number;
  value?: string | number;
  color?: string;
}

interface RechartsLabelProps {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: string | number;
}

const defaultRevenueChartOptions: RevenueChartOptions = {
  receitaTotal: true,
  comprasEcommerce: true,
  investimentoGoogle: false,
  investimentoMeta: false,
  roas: false,
  investimentoTotal: true,
};

const revenueSeriesColor = "#3b82f6";
const revenueBarFill = "rgba(59, 130, 246, 0.72)";
const purchasesSeriesColor = "#f97316";
const purchasesLineColor = "rgba(249, 115, 22, 0.78)";

// Funções de formatação usando utilitário centralizado
const formatCurrency = (value: number) => {
  return formatCurrencyUtil(value);
};

const formatCurrencyFull = (value: number) => {
  return formatCurrencyUtil(value);
};

const formatNumber = (value: number) => {
  return formatInteger(value);
};

const formatRevenueBarValue = (value: number) => {
  const absoluteValue = Math.abs(value);

  if (absoluteValue >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mi`;
  }

  if (absoluteValue >= 1_000) {
    return `${(value / 1_000).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mil`;
  }

  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const renderRevenueBarLabel = ({
  x,
  y,
  width,
  height,
  value,
}: RechartsLabelProps) => {
  const numericValue = Number(value) || 0;
  const barHeight = Number(height) || 0;
  const barWidth = Number(width) || 0;

  if (barHeight < 56 || barWidth < 42 || numericValue <= 0) {
    return <g />;
  }

  const centerX = Number(x) + barWidth / 2;
  const topY = Number(y) + 12;

  return (
    <text
      x={centerX}
      y={topY}
      textAnchor="middle"
      fill="#ffffff"
      fontWeight={700}
    >
      <tspan x={centerX} dy={0} fontSize={11}>
        R$
      </tspan>
      <tspan x={centerX} dy={16} fontSize={14}>
        {formatRevenueBarValue(numericValue)}
      </tspan>
    </text>
  );
};

const ReceitaPage: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const comparisonEnabled = isComparisonEnabled(filterState);
  const { selectedAccount } = useAccount();
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();
  const customerUuid =
    selectedAccount?.customer_uuid ||
    localStorage.getItem("active_customer_uuid") ||
    null;
  const accountId = selectedAccount?.account_id ?? undefined;
  // Começa carregando: o primeiro desenho acontece antes de o efeito ligar a flag, e o card
  // piscava N/D antes de a busca sair.
  const [loading, setLoading] = useState<boolean>(true);
  const [erroReceita, setErroReceita] = useState<boolean>(false);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [receitaPorDiaData, setReceitaPorDiaData] = useState<
    DailyRevenueChartRow[]
  >([]);
  const [receitaPorDiaSemanaData, setReceitaPorDiaSemanaData] = useState<
    WeekdayRevenueChartRow[]
  >([]);
  const [receitaPorHorasData, setReceitaPorHorasData] = useState<
    HourRevenueChartRow[]
  >([]);
  const [receitaPorMesData, setReceitaPorMesData] = useState<
    MonthRevenueChartRow[]
  >([]);
  const [dailyChartOptions, setDailyChartOptions] =
    useState<RevenueChartOptions>({
      ...defaultRevenueChartOptions,
    });
  const [chartOptions, setChartOptions] = useState<RevenueChartOptions>({
    ...defaultRevenueChartOptions,
  });

  const startDate = useMemo(
    () => formatDateToISO(filterState.dateRange.startDate),
    [filterState.dateRange.startDate],
  );
  const endDate = useMemo(
    () => formatDateToISO(filterState.dateRange.endDate),
    [filterState.dateRange.endDate],
  );

  useEffect(() => {
    const fetchData = async () => {
      const comparison =
        filterState.comparison?.type === "previousYear"
          ? "previous_year"
          : "previous_period";
      const customerUuid =
        selectedAccount?.customer_uuid ||
        localStorage.getItem("active_customer_uuid") ||
        undefined;
      if (!customerUuid) {
        setKpis(null);
        setReceitaPorDiaData([]);
        setReceitaPorDiaSemanaData([]);
        setReceitaPorHorasData([]);
        setReceitaPorMesData([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setErroReceita(false);
        const { data } = await getRevenueDashboardApiV1RevenueDashboardGet({
          query: {
            start_date: startDate,
            end_date: endDate,
            comparison,
            customer_uuid: customerUuid,
            account_id: accountId,
          },
          throwOnError: true,
        });

        setKpis(data?.kpis || null);

        // Daily
        const perDay = (data?.charts?.per_day || []).map(
          (d: RevenueDashboardPerDay): DailyRevenueChartRow => {
            const label = formatDateBR(d.date_label);
            return {
              data: label,
              receitaTotal: Number(d.total_revenue || 0),
              comprasEcommerce: Number(d.orders || 0),
              investimentoGoogle: Number(d.google_investment || 0),
              investimentoMeta: Number(d.meta_investment || 0),
              roas: Number(d.roas || 0),
            };
          },
        );
        setReceitaPorDiaData(perDay);

        // Weekday
        const perWeekday = (data?.charts?.per_weekday || []).map(
          (d: RevenueDashboardPerWeekday): WeekdayRevenueChartRow => ({
            dia: d.weekday_label,
            receitaTotal: Number(d.total_revenue || 0),
            comprasEcommerce: Number(d.orders || 0),
            investimentoGoogle: Number(d.google_investment || 0),
            investimentoMeta: Number(d.meta_investment || 0),
            roas: Number(d.roas || 0),
          }),
        );
        setReceitaPorDiaSemanaData(perWeekday);

        // Hour
        const perHour = (data?.charts?.per_hour || []).map(
          (d: RevenueDashboardPerHour): HourRevenueChartRow => ({
            hora: String(d.hour).padStart(2, "0"),
            receitaTotal: Number(d.total_revenue || 0),
            comprasEcommerce: Number(d.orders || 0),
          }),
        );
        setReceitaPorHorasData(perHour);

        // Month
        const perMonth = (data?.charts?.per_month || []).map(
          (d: RevenueDashboardPerMonth): MonthRevenueChartRow => {
            const label = formatMonthLabel(d.month_label);
            return {
              mes: label,
              receitaTotal: Number(d.total_revenue || 0),
              transacoes: Number(d.transactions || 0),
            };
          },
        );
        setReceitaPorMesData(perMonth);
      } catch (err) {
        console.error("Erro ao carregar dados de receita:", err);
        setKpis(null);
        setErroReceita(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [
    startDate,
    endDate,
    filterState.comparison?.type,
    selectedAccount,
    accountId,
    campaignFiltersNonce,
  ]);

  const handleDailyChartOptionChange = (option: keyof RevenueChartOptions) => {
    setDailyChartOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  const handleChartOptionChange = (option: keyof RevenueChartOptions) => {
    setChartOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  return (
    <div className="p-6 space-y-6 page-background">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Receita</h1>
          <p className="text-muted-foreground">
            Análise detalhada da receita do seu e-commerce
          </p>
        </div>
        <Button variant="outline" className="text-muted-foreground">
          LIMPAR FILTRO
        </Button>
      </div>

      {/* Filtro Global */}
      <GlobalFilter
        className="mb-6"
        rightSlot={
          <CampaignFiltersControl
            customerUuid={customerUuid}
            accountId={accountId}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />

      {/* Card RECEITA TOTAL - largura total */}
      <Card className="card-kpi-primary">
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-1">
              <span className="truncate">Receita Total</span>
              <HelpTooltip
                content={getKpiTooltip("Receita Total")}
                triggerClassName="h-3.5 w-3.5 text-[9px]"
              />
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <CarregandoValor className="text-2xl" />
          ) : kpis && Number(kpis.total_revenue || 0) > 0 ? (
            <div className="flex">
              <KpiNumber
                value={Number(kpis.total_revenue)}
                format={(n) => formatCurrencyFull(Number(n))}
                className="font-light text-foreground"
                autoFit={true}
                maxFontPx={32}
                minFontPx={18}
                breathingRoomPx={8}
                ariaLabel="Receita total"
                showComparison={comparisonEnabled}
                changePercentage={Number(
                  kpis.total_revenue_change_percentage || 0,
                )}
                trendIndicator={
                  kpis.total_revenue_trend_indicator === "up"
                    ? "up"
                    : kpis.total_revenue_trend_indicator === "down"
                      ? "down"
                      : "neutral"
                }
              />
            </div>
          ) : (
            <div className="text-muted-foreground">
              <ValorIndisponivel
                fontes={FONTES_DA_RECEITA}
                erro={erroReceita}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gráfico RECEITA POR DIA - largura total */}
      <Card className="card-kpi-secondary">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-center flex-1">
              RECEITA POR DIA
            </CardTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-muted-foreground"
                >
                  <BarChart3 className="h-4 w-4 mr-1" />
                  Métricas
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuCheckboxItem
                  checked={dailyChartOptions.receitaTotal}
                  onCheckedChange={() =>
                    handleDailyChartOptionChange("receitaTotal")
                  }
                >
                  Receita Total
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={dailyChartOptions.comprasEcommerce}
                  onCheckedChange={() =>
                    handleDailyChartOptionChange("comprasEcommerce")
                  }
                >
                  Compras do e-commerce
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={dailyChartOptions.investimentoGoogle}
                  onCheckedChange={() =>
                    handleDailyChartOptionChange("investimentoGoogle")
                  }
                >
                  Investimento Google
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={dailyChartOptions.investimentoMeta}
                  onCheckedChange={() =>
                    handleDailyChartOptionChange("investimentoMeta")
                  }
                >
                  Investimento Meta
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={dailyChartOptions.roas}
                  onCheckedChange={() => handleDailyChartOptionChange("roas")}
                >
                  ROAS
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={dailyChartOptions.investimentoTotal}
                  onCheckedChange={() =>
                    handleDailyChartOptionChange("investimentoTotal")
                  }
                >
                  Investimento Total (Meta + Google)
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[350px] w-full" />
          ) : (
            <ScrollableChart minWidth="640px">
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart
                  data={receitaPorDiaData.map((d) => ({
                    ...d,
                    investimentoTotal:
                      (Number(d.investimentoGoogle) || 0) +
                      (Number(d.investimentoMeta) || 0),
                  }))}
                  margin={{ top: 10, right: 50, left: 10, bottom: 0 }}
                  barCategoryGap="15%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.06)"
                  />
                  <XAxis
                    dataKey="data"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  {/* Eixo Y Esquerdo: Investimento Meta, Google, ROAS e Compras */}
                  <YAxis
                    yAxisId="left"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, "auto"]}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `${(v / 1000).toLocaleString("pt-BR")}k`
                        : String(v)
                    }
                  />
                  {/* Eixo Y Direito: Receita Total e Investimento Total */}
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: revenueSeriesColor, fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, "auto"]}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `${(v / 1000).toLocaleString("pt-BR")}k`
                        : String(v)
                    }
                  />
                  <RechartsTooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const LABELS: Record<string, string> = {
                        receitaTotal: "Receita Total",
                        comprasEcommerce: "Compras do e-commerce",
                        investimentoTotal: "Investimento Total (Meta + Google)",
                        investimentoGoogle: "Investimento Google",
                        investimentoMeta: "Investimento Meta",
                        roas: "ROAS",
                      };
                      const COLORS: Record<string, string> = {
                        receitaTotal: revenueSeriesColor,
                        comprasEcommerce: purchasesSeriesColor,
                        investimentoTotal: "#eab308",
                        investimentoGoogle: "#10b981",
                        investimentoMeta: "#8b5cf6",
                        roas: "#ef4444",
                      };
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                          <p className="text-gray-500 text-xs mb-1">{label}</p>
                          <table style={{ borderSpacing: "8px 2px" }}>
                            <tbody>
                              {payload.map((entry) => {
                                const key = entry.dataKey as string;
                                const formatted =
                                  key === "roas"
                                    ? Number(entry.value).toLocaleString(
                                        "pt-BR",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      )
                                    : key === "comprasEcommerce"
                                      ? Number(entry.value).toLocaleString(
                                          "pt-BR",
                                        )
                                      : formatCurrency(Number(entry.value));
                                return (
                                  <tr key={key}>
                                    <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                      <span
                                        className="inline-block w-2.5 h-2.5 rounded-full"
                                        style={{
                                          backgroundColor:
                                            COLORS[key] || entry.color,
                                        }}
                                      />
                                      {LABELS[key] || key}:
                                    </td>
                                    <td className="text-right font-semibold text-gray-900 pl-3">
                                      {formatted}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                  <RechartsLegend
                    content={() => {
                      const items: {
                        key: string;
                        label: string;
                        color: string;
                      }[] = [
                        {
                          key: "receitaTotal",
                          label: "Receita Total",
                          color: revenueSeriesColor,
                        },
                        {
                          key: "comprasEcommerce",
                          label: "Compras do e-commerce",
                          color: purchasesSeriesColor,
                        },
                        {
                          key: "investimentoGoogle",
                          label: "Investimento Google",
                          color: "#10b981",
                        },
                        {
                          key: "investimentoMeta",
                          label: "Investimento Meta",
                          color: "#8b5cf6",
                        },
                        { key: "roas", label: "ROAS", color: "#ef4444" },
                        {
                          key: "investimentoTotal",
                          label: "Investimento Total (Meta + Google)",
                          color: "#eab308",
                        },
                      ];
                      const active = items.filter(
                        (i) =>
                          dailyChartOptions[i.key as keyof RevenueChartOptions],
                      );
                      if (active.length === 0) return null;
                      return (
                        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 pb-2">
                          {active.map(({ key, label, color }) => (
                            <div
                              key={key}
                              className="flex items-center gap-1.5"
                            >
                              <span
                                className="inline-block h-2.5 w-2.5 rounded-full"
                                style={{ backgroundColor: color }}
                              />
                              <span className="text-xs text-gray-600">
                                {label}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    }}
                  />
                  {/* Barras: Receita Total no eixo direito */}
                  {dailyChartOptions.receitaTotal && (
                    <Bar
                      yAxisId="right"
                      dataKey="receitaTotal"
                      fill={revenueBarFill}
                      radius={[3, 3, 0, 0]}
                      name="receitaTotal"
                      label={renderRevenueBarLabel}
                    />
                  )}
                  {/* Linha: Compras do e-commerce no eixo esquerdo */}
                  {dailyChartOptions.comprasEcommerce && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="comprasEcommerce"
                      stroke={purchasesLineColor}
                      strokeWidth={2}
                      dot={{
                        r: 3,
                        fill: purchasesLineColor,
                        stroke: purchasesLineColor,
                      }}
                      name="comprasEcommerce"
                    />
                  )}
                  {/* Linhas no eixo esquerdo: Investimentos e ROAS */}
                  {dailyChartOptions.investimentoGoogle && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="investimentoGoogle"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "#10b981" }}
                      name="investimentoGoogle"
                    />
                  )}
                  {dailyChartOptions.investimentoMeta && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="investimentoMeta"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "#8b5cf6" }}
                      name="investimentoMeta"
                    />
                  )}
                  {dailyChartOptions.roas && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="roas"
                      stroke="#ef4444"
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      dot={{ r: 3, fill: "#ef4444" }}
                      name="roas"
                    />
                  )}
                  {/* Linha no eixo direito: Investimento Total */}
                  {dailyChartOptions.investimentoTotal && (
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="investimentoTotal"
                      stroke="#eab308"
                      strokeWidth={2.5}
                      dot={false}
                      name="investimentoTotal"
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </ScrollableChart>
          )}
        </CardContent>
      </Card>

      {/* TERCEIRA SEÇÃO: RECEITA POR DIA DA SEMANA */}
      <Card className="card-kpi-secondary">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-center flex-1">
              RECEITA POR DIA DA SEMANA
            </CardTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-muted-foreground"
                >
                  <BarChart3 className="h-4 w-4 mr-1" />
                  Métricas
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuCheckboxItem
                  checked={chartOptions.receitaTotal}
                  onCheckedChange={() =>
                    handleChartOptionChange("receitaTotal")
                  }
                >
                  Receita Total
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={chartOptions.comprasEcommerce}
                  onCheckedChange={() =>
                    handleChartOptionChange("comprasEcommerce")
                  }
                >
                  Compras do e-commerce
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={chartOptions.investimentoGoogle}
                  onCheckedChange={() =>
                    handleChartOptionChange("investimentoGoogle")
                  }
                >
                  Investimento Google
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={chartOptions.investimentoMeta}
                  onCheckedChange={() =>
                    handleChartOptionChange("investimentoMeta")
                  }
                >
                  Investimento Meta
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={chartOptions.roas}
                  onCheckedChange={() => handleChartOptionChange("roas")}
                >
                  ROAS
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={chartOptions.investimentoTotal}
                  onCheckedChange={() =>
                    handleChartOptionChange("investimentoTotal")
                  }
                >
                  Investimento Total (Meta + Google)
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[350px] w-full" />
          ) : (
            <ScrollableChart minWidth="640px">
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart
                  data={receitaPorDiaSemanaData.map((d) => ({
                    ...d,
                    investimentoTotal:
                      (Number(d.investimentoGoogle) || 0) +
                      (Number(d.investimentoMeta) || 0),
                  }))}
                  margin={{ top: 10, right: 50, left: 10, bottom: 0 }}
                  barCategoryGap="15%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.06)"
                  />
                  <XAxis
                    dataKey="dia"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, "auto"]}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `${(v / 1000).toLocaleString("pt-BR")}k`
                        : String(v)
                    }
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: revenueSeriesColor, fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, "auto"]}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `${(v / 1000).toLocaleString("pt-BR")}k`
                        : String(v)
                    }
                  />
                  <RechartsTooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const LABELS: Record<string, string> = {
                        receitaTotal: "Receita Total",
                        comprasEcommerce: "Compras do e-commerce",
                        investimentoTotal: "Investimento Total (Meta + Google)",
                        investimentoGoogle: "Investimento Google",
                        investimentoMeta: "Investimento Meta",
                        roas: "ROAS",
                      };
                      const COLORS: Record<string, string> = {
                        receitaTotal: revenueSeriesColor,
                        comprasEcommerce: purchasesSeriesColor,
                        investimentoTotal: "#eab308",
                        investimentoGoogle: "#10b981",
                        investimentoMeta: "#8b5cf6",
                        roas: "#ef4444",
                      };
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                          <p className="text-gray-500 text-xs mb-1">{label}</p>
                          <table style={{ borderSpacing: "8px 2px" }}>
                            <tbody>
                              {payload.map((entry) => {
                                const key = entry.dataKey as string;
                                const formatted =
                                  key === "roas"
                                    ? Number(entry.value).toLocaleString(
                                        "pt-BR",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      )
                                    : key === "comprasEcommerce"
                                      ? Number(entry.value).toLocaleString(
                                          "pt-BR",
                                        )
                                      : formatCurrency(Number(entry.value));
                                return (
                                  <tr key={key}>
                                    <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                      <span
                                        className="inline-block w-2.5 h-2.5 rounded-full"
                                        style={{
                                          backgroundColor:
                                            COLORS[key] || entry.color,
                                        }}
                                      />
                                      {LABELS[key] || key}:
                                    </td>
                                    <td className="text-right font-semibold text-gray-900 pl-3">
                                      {formatted}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                  <RechartsLegend
                    content={() => {
                      const items: {
                        key: string;
                        label: string;
                        color: string;
                      }[] = [
                        {
                          key: "receitaTotal",
                          label: "Receita Total",
                          color: revenueSeriesColor,
                        },
                        {
                          key: "comprasEcommerce",
                          label: "Compras do e-commerce",
                          color: purchasesSeriesColor,
                        },
                        {
                          key: "investimentoGoogle",
                          label: "Investimento Google",
                          color: "#10b981",
                        },
                        {
                          key: "investimentoMeta",
                          label: "Investimento Meta",
                          color: "#8b5cf6",
                        },
                        { key: "roas", label: "ROAS", color: "#ef4444" },
                        {
                          key: "investimentoTotal",
                          label: "Investimento Total (Meta + Google)",
                          color: "#eab308",
                        },
                      ];
                      const active = items.filter(
                        (i) => chartOptions[i.key as keyof typeof chartOptions],
                      );
                      if (active.length === 0) return null;
                      return (
                        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 pb-2">
                          {active.map(({ key, label, color }) => (
                            <div
                              key={key}
                              className="flex items-center gap-1.5"
                            >
                              <span
                                className="inline-block h-2.5 w-2.5 rounded-full"
                                style={{ backgroundColor: color }}
                              />
                              <span className="text-xs text-gray-600">
                                {label}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    }}
                  />
                  {chartOptions.receitaTotal && (
                    <Bar
                      yAxisId="right"
                      dataKey="receitaTotal"
                      fill={revenueBarFill}
                      radius={[3, 3, 0, 0]}
                      name="receitaTotal"
                      label={renderRevenueBarLabel}
                    />
                  )}
                  {chartOptions.comprasEcommerce && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="comprasEcommerce"
                      stroke={purchasesLineColor}
                      strokeWidth={2}
                      dot={{
                        r: 3,
                        fill: purchasesLineColor,
                        stroke: purchasesLineColor,
                      }}
                      name="comprasEcommerce"
                    />
                  )}
                  {chartOptions.investimentoGoogle && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="investimentoGoogle"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "#10b981" }}
                      name="investimentoGoogle"
                    />
                  )}
                  {chartOptions.investimentoMeta && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="investimentoMeta"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "#8b5cf6" }}
                      name="investimentoMeta"
                    />
                  )}
                  {chartOptions.roas && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="roas"
                      stroke="#ef4444"
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      dot={{ r: 3, fill: "#ef4444" }}
                      name="roas"
                    />
                  )}
                  {chartOptions.investimentoTotal && (
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="investimentoTotal"
                      stroke="#eab308"
                      strokeWidth={2.5}
                      dot={false}
                      name="investimentoTotal"
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </ScrollableChart>
          )}
        </CardContent>
      </Card>

      {/* QUARTA SEÇÃO: RECEITA POR HORAS */}
      <Card className="card-kpi-secondary">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-center flex-1">
              RECEITA POR HORAS
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[350px] w-full" />
          ) : receitaPorHorasData.length === 0 ? (
            <div className="flex items-center justify-center h-[350px] text-muted-foreground text-sm">
              Dados por hora não disponíveis para o período selecionado
            </div>
          ) : (
            <ScrollableChart minWidth="640px">
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart
                  data={receitaPorHorasData}
                  margin={{ top: 10, right: 50, left: 10, bottom: 0 }}
                  barCategoryGap="15%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.06)"
                  />
                  <XAxis
                    dataKey="hora"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  {/* Eixo Y Esquerdo: Receita Total (barras) */}
                  <YAxis
                    yAxisId="left"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, "auto"]}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `${(v / 1000).toLocaleString("pt-BR")}k`
                        : String(v)
                    }
                  />
                  {/* Eixo Y Direito: Compras de e-commerce */}
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: purchasesSeriesColor, fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, "auto"]}
                  />
                  <RechartsTooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const LABELS: Record<string, string> = {
                        receitaTotal: "Receita Total",
                        comprasEcommerce: "Compras de e-commerce",
                      };
                      const COLORS: Record<string, string> = {
                        receitaTotal: revenueSeriesColor,
                        comprasEcommerce: purchasesSeriesColor,
                      };
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                          <p className="text-gray-500 text-xs mb-1">{label}</p>
                          <table style={{ borderSpacing: "8px 2px" }}>
                            <tbody>
                              {payload.map((entry) => {
                                const key = entry.dataKey as string;
                                const formatted =
                                  key === "comprasEcommerce"
                                    ? Number(entry.value).toLocaleString(
                                        "pt-BR",
                                      )
                                    : formatCurrency(Number(entry.value));
                                return (
                                  <tr key={key}>
                                    <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                      <span
                                        className="inline-block w-2.5 h-2.5 rounded-full"
                                        style={{
                                          backgroundColor:
                                            COLORS[key] || entry.color,
                                        }}
                                      />
                                      {LABELS[key] || key}:
                                    </td>
                                    <td className="text-right font-semibold text-gray-900 pl-3">
                                      {formatted}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                  <RechartsLegend
                    content={() => (
                      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 pb-2">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="inline-block h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: purchasesSeriesColor }}
                          />
                          <span className="text-xs text-gray-600">
                            Compras de e-commerce
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span
                            className="inline-block h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: revenueSeriesColor }}
                          />
                          <span className="text-xs text-gray-600">
                            Receita Total
                          </span>
                        </div>
                      </div>
                    )}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="receitaTotal"
                    fill={revenueBarFill}
                    radius={[3, 3, 0, 0]}
                    name="receitaTotal"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="comprasEcommerce"
                    stroke={purchasesLineColor}
                    strokeWidth={2}
                    dot={{
                      r: 4,
                      fill: purchasesLineColor,
                      stroke: purchasesLineColor,
                    }}
                    activeDot={{
                      r: 6,
                      fill: purchasesLineColor,
                      stroke: purchasesLineColor,
                    }}
                    name="comprasEcommerce"
                    label={({ x, y, value }: RechartsLabelProps) => {
                      const yPosition = Number(y);
                      if (!Number.isFinite(yPosition)) return <g />;
                      return (
                        <text
                          x={x}
                          y={yPosition - 10}
                          textAnchor="middle"
                          fill={purchasesLineColor}
                          fontSize={10}
                          fontWeight={600}
                        >
                          {Number(value).toLocaleString("pt-BR")}
                        </text>
                      );
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ScrollableChart>
          )}
        </CardContent>
      </Card>

      {/* QUINTA SEÇÃO: RECEITA POR MÊS */}
      <Card className="card-kpi-secondary">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-center flex-1">
              RECEITA POR MÊS
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[350px] w-full" />
          ) : receitaPorMesData.length === 0 ? (
            <div className="flex items-center justify-center h-[350px] text-muted-foreground text-sm">
              Dados mensais não disponíveis para o período selecionado
            </div>
          ) : (
            <ScrollableChart minWidth="640px">
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart
                  data={receitaPorMesData}
                  margin={{ top: 30, right: 20, left: 10, bottom: 0 }}
                  barCategoryGap="15%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.06)"
                  />
                  <XAxis
                    dataKey="mes"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.2)]}
                    tickFormatter={(v: number) => {
                      if (v >= 1000000)
                        return `${(v / 1000000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mi`;
                      if (v >= 1000)
                        return `${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} mil`;
                      return String(v);
                    }}
                  />
                  <RechartsTooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const LABELS: Record<string, string> = {
                        receitaTotal: "Receita Total",
                        transacoes: "Transações",
                      };
                      const COLORS: Record<string, string> = {
                        receitaTotal: revenueSeriesColor,
                        transacoes: "#fb923c",
                      };
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                          <p className="text-gray-500 text-xs mb-1">{label}</p>
                          <table style={{ borderSpacing: "8px 2px" }}>
                            <tbody>
                              {payload.map((entry) => {
                                const key = entry.dataKey as string;
                                const formatted =
                                  key === "transacoes"
                                    ? Number(entry.value).toLocaleString(
                                        "pt-BR",
                                      )
                                    : formatCurrency(Number(entry.value));
                                return (
                                  <tr key={key}>
                                    <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                      <span
                                        className="inline-block w-2.5 h-2.5 rounded-full"
                                        style={{
                                          backgroundColor:
                                            COLORS[key] || entry.color,
                                        }}
                                      />
                                      {LABELS[key] || key}:
                                    </td>
                                    <td className="text-right font-semibold text-gray-900 pl-3">
                                      {formatted}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                  <RechartsLegend
                    content={() => (
                      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 pb-2">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="inline-block h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: "#fb923c" }}
                          />
                          <span className="text-xs text-gray-600">
                            Transações
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span
                            className="inline-block h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: revenueSeriesColor }}
                          />
                          <span className="text-xs text-gray-600">
                            Receita Total
                          </span>
                        </div>
                      </div>
                    )}
                  />
                  <Bar
                    dataKey="receitaTotal"
                    fill={revenueBarFill}
                    radius={[3, 3, 0, 0]}
                    name="receitaTotal"
                    label={({
                      x,
                      y,
                      width,
                      height,
                      value,
                    }: RechartsLabelProps) => {
                      const v = Number(value) || 0;
                      const xPosition = Number(x);
                      const yPosition = Number(y);
                      const barWidth = Number(width);
                      const barHeight = Number(height);
                      if (
                        !Number.isFinite(xPosition) ||
                        !Number.isFinite(yPosition) ||
                        !Number.isFinite(barWidth) ||
                        !Number.isFinite(barHeight)
                      ) {
                        return <g />;
                      }
                      return (
                        <text
                          x={xPosition + barWidth / 2}
                          y={yPosition + barHeight / 2}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#ffffff"
                          fontSize={14}
                          fontWeight={700}
                        >
                          {formatCurrency(v)}
                        </text>
                      );
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="transacoes"
                    stroke="#fb923c"
                    strokeWidth={2}
                    dot={{ r: 5, fill: "#fb923c", stroke: "#fb923c" }}
                    activeDot={{ r: 7 }}
                    name="transacoes"
                    label={({ x, y, value }: RechartsLabelProps) => {
                      const yPosition = Number(y);
                      if (!Number.isFinite(yPosition)) return <g />;
                      return (
                        <text
                          x={x}
                          y={yPosition - 12}
                          textAnchor="middle"
                          fill="#fb923c"
                          fontSize={11}
                          fontWeight={600}
                        >
                          {Number(value).toLocaleString("pt-BR")}
                        </text>
                      );
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ScrollableChart>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReceitaPage;
